import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { AlertMessagesContext } from 'react-alert-messages';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Button,
  TextField,
  MenuItem,
  IconButton,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import CustomerService from 'services/CustomerService';
import EstimateService from 'services/EstimateService';
import { TaxHelper } from 'hooks/TaxHelper';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ROUTES, PAPER_SIZE, COUNTRY_CONFIGRATION, PRINT_TYPES } from 'const';
import { CartHelper } from 'hooks/CartHelper';
import { InventoryContext } from 'Context/InventoryContext';
import { ShopContext } from 'Context/ShopContext';
import { BillFormatContext } from 'Context/BillFormatContext';
import { TerminalContext } from 'Context/TerminalContext';
import ShopsHelper from 'helpers/ShopsHelper';
import SalesPersonService from 'services/SalesPersonService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import NumberField from 'components/library/NumberField';
import { useTranslation } from 'react-i18next';
import { formatBalanceWithCr } from 'helpers/NumberFormatHelper';
import EstimateUpdateSuccessDialog from 'components/popups/EstimateUpdateSuccessDialog';
import NewCustomerDialog from 'components/popups/NewCustomerDialog';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EstimateHelper from 'helpers/EstimateHelper';
import PrintDocument from 'components/utils/PrintDocument/PrintDocument';

function AddEstimate() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { products, getProducts } = useContext(InventoryContext);
  const { getBillFormat } = useContext(BillFormatContext);
  const {
    terminals: terminalsFromContext,
    selectedTerminal: selectedTerminalFromContext,
    setSelectedTerminal: setSelectedTerminalInContext,
  } = useContext(TerminalContext);

  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');
  const { shop } = useContext(ShopContext);
  const TaxManager = TaxHelper();
  const CartManager = CartHelper();
  const [estimateItems, setEstimateItems] = useState([]);
  const [estimateNoPrefix, setEstimateNoPrefix] = useState('');
  const [estimateNoSuffix, setEstimateNoSuffix] = useState('');
  const [itemName, setItemName] = useState('');
  const [rate, setRate] = useState('');
  const [stock, setStock] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [tax, setTax] = useState(0);
  const [cess, setCess] = useState(0);
  const [item, setItem] = useState(null);
  const [totalAmount, setTotalAmount] = useState('');
  const [editData, setEditData] = useState({});
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [billDate, setBillDate] = useState(new Date());
  const [estimateTotals, setEstimateTotals] = useState({
    payable: 0,
    tax: 0,
    cess: 0,
  });
  const [estimateId, setEstimateId] = useState(id ?? null);
  const [salespersons, setSalespersons] = useState([]);
  const [selectedSalespersonId, setSelectedSalespersonId] = useState(null);
  const [selectedWaiter, setSelectedWaiter] = useState(null);
  const [description, setDescription] = useState('');
  const [remark, setRemark] = useState('');
  const [status, setStatus] = useState('draft');
  const [discountAppliedOnItems, setDiscountAppliedOnItems] = useState(0);
  const [updateSuccessData, setUpdateSuccessData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isShowNewCustomerDialog, setIsShowNewCustomerDialog] = useState(false);
  const [estimateToPrint, setEstimateToPrint] = useState(null);
  const [billFormatToPrint, setBillFormatToPrint] = useState(null);
  const [totalsToPrint, setTotalsToPrint] = useState(null);
  const [paperSize, setPaperSize] = useState(PAPER_SIZE.A4);
  const [customerGroups, setCustomerGroups] = useState([]);
  const isMasterDataLoaded = useRef(false);

  const deleteItem = (index) => {
    setEstimateItems(estimateItems.filter((_, idx) => idx !== index));
    setHasUnsavedChanges(true);
  };

  const resetItemFields = () => {
    setItemName('');
    setRate('');
    setQuantity(1);
    setTax('');
    setDescription('');
    setCess('');
    setTotalAmount('');
    setItem(null);
    setSelectedSalespersonId(null);
  };

  const clearEstimateHandler = () => {
    setBillDate(new Date());
    setEstimateId(null);
    setEstimateNoSuffix((prev) => Number(prev) + 1);
    setSelectedCustomer(null);
    setSelectedWaiter(null);
    resetItemFields();
    setEstimateItems([]);
    setEditData({});
    setRemark('');
    setStatus('draft');
    setDiscountAppliedOnItems(0);
    setUpdateSuccessData(null);
    setHasUnsavedChanges(false);
  };

  const addItem = () => {
    const data = {
      item_id: item ? item.id : null,
      rate,
      quantity,
      tax: tax === '' ? 0 : tax ?? 0,
      cess: cess === '' ? 0 : cess ?? 0,
      payable: Number(totalAmount.toString().replace(/[^0-9.]/g, '')),
      name: item ? itemName : '',
      item,
      description,
      waiter_id: selectedSalespersonId,
      stock,
    };

    setEstimateItems([...estimateItems, data]);
    setHasUnsavedChanges(true);
    resetItemFields();
  };

  const getCustomers = useCallback(async (newCustomer) => {
    try {
      const res = await CustomerService.getCustomers();
      setCustomers(res);
      if (newCustomer) {
        const found = res.find((c) => c.id === newCustomer.id);
        if (found) {
          setSelectedCustomer(found);
        } else {
          setSelectedCustomer(newCustomer);
        }
      }
    } catch (err) {}
  }, []);

  const getCustomerGroups = useCallback(async () => {
    try {
      const res = await CustomerService.getCustomerGroups();
      setCustomerGroups(res);
    } catch (err) {}
  }, []);

  const getSalespersons = useCallback(async () => {
    const response = await SalesPersonService.getSalesPersons();
    setSalespersons(response);
  }, []);

  const getNextEstimateNo = useCallback(
    async (prefix) => {
      setLoadingIndicator(true);
      try {
        const response = await EstimateService.getHighestEstimateNumber(prefix);
        setEstimateNoSuffix(response?.highest_estimate_no + 1);
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [postAlertMessage]
  );

  const getMasterData = useCallback(async () => {
    setLoadingIndicator(true);
    await Promise.all([getProducts(), getCustomers(), getSalespersons(), getCustomerGroups()]);
    setLoadingIndicator(false);
  }, [getProducts, getCustomers, getSalespersons, getCustomerGroups]);

  const handlePrint = async (estimate_id_or_no, size = PAPER_SIZE.A4) => {
    setLoadingIndicator(true);
    try {
      const parsedSize =
        size === PAPER_SIZE.A4
          ? PAPER_SIZE.A4
          : size === PAPER_SIZE.A5_VERTICAL
          ? PAPER_SIZE.A5_VERTICAL
          : size === PAPER_SIZE.A5_HORIZONTAL
          ? PAPER_SIZE.A5_HORIZONTAL
          : PAPER_SIZE.THERMAL_80MM;
      setPaperSize(parsedSize);

      let fullEstimate;
      if (
        typeof estimate_id_or_no === 'number' ||
        (typeof estimate_id_or_no === 'string' && !isNaN(estimate_id_or_no))
      ) {
        fullEstimate = await EstimateService.getEstimate(estimate_id_or_no);
      } else {
        const estimates = await EstimateService.getEstimates({ search: estimate_id_or_no });
        if (estimates && estimates.length > 0) {
          fullEstimate = await EstimateService.getEstimate(estimates[0].id);
        }
      }

      if (!fullEstimate) {
        postAlertMessage({ text: 'Estimate not found', type: 'failed' });
        setLoadingIndicator(false);
        return;
      }

      const terminalId = fullEstimate.initiated_terminal_id || selectedTerminalFromContext?.id;
      let currentBillFormat = null;
      if (terminalId) {
        currentBillFormat = await getBillFormat(terminalId);
      } else if (terminalsFromContext && terminalsFromContext.length > 0) {
        currentBillFormat = await getBillFormat(terminalsFromContext[0].id);
      }

      setEstimateToPrint(fullEstimate);
      setBillFormatToPrint(currentBillFormat);

      const totals = (fullEstimate.items || []).reduce(
        (acc, item) => {
          acc.payable += item.payable || 0;
          acc.tax += item.tax || 0;
          acc.cess += item.cess || 0;
          return acc;
        },
        { payable: 0, tax: 0, cess: 0 }
      );

      setTotalsToPrint(totals);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  useEffect(() => {
    if (estimateToPrint && billFormatToPrint && totalsToPrint) {
      document.body.classList.add('printing-invoice');
      setTimeout(() => {
        window.print();
        document.body.classList.remove('printing-invoice');
        setEstimateToPrint(null);
        setBillFormatToPrint(null);
        setTotalsToPrint(null);
      }, 500);
    }
  }, [estimateToPrint, billFormatToPrint, totalsToPrint]);

  const handleSaveEstimate = async () => {
    setLoadingIndicator(true);
    try {
      if (estimateItems.length === 0) {
        postAlertMessage({
          text: tOperations('add_at_least_one_item'),
          type: 'failed',
        });
        return;
      }

      const estimateData = {
        ...editData,
        time: DateTimeHelpers.getTimezoneOffsetDate(billDate).toISOString(),
        payable: estimateTotals?.payable,
        total_amount: estimateTotals?.payable,
        tax: estimateTotals?.tax,
        cess: estimateTotals?.cess,
        customer_id: selectedCustomer?.id || null,
        waiter_id: selectedWaiter?.id || null,
        initiated_terminal_id: selectedTerminalFromContext?.id || null,
        estimate_no: `${estimateNoPrefix}${estimateNoSuffix}`,
        estimate_no_suffix: Number(estimateNoSuffix),
        items: estimateItems.map((item) => ({
          item_id: item.item_id || item.item?.id,
          quantity: item.quantity,
          rate: item.rate,
          tax: item.tax,
          cess: item.cess,
          payable: item.payable,
          description: item.description,
          waiter_id: item.waiter_id || selectedWaiter?.id,
        })),
        remark,
        status,
      };

      if (estimateId) {
        await EstimateService.updateEstimate(estimateId, estimateData);
        postAlertMessage({ text: 'Estimate updated successfully', type: 'success' });
        setUpdateSuccessData({ action: 'Update', invoiceNo: estimateData.estimate_no, estimate_id: estimateId });
      } else {
        const response = await EstimateService.createEstimate(estimateData);
        if (response && response.id) {
          setEstimateId(response.id);
          setEditData(response);
        }
        postAlertMessage({ text: 'Estimate created successfully', type: 'success' });
        setUpdateSuccessData({ action: 'Create', invoiceNo: estimateData.estimate_no, estimate_id: response.id });
      }
      setHasUnsavedChanges(false);

      // clearEstimateHandler(); // Moved to dialog close or handled by state
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    } finally {
      setLoadingIndicator(false);
    }
  };

  const handleConvertToSale = () => {
    EstimateHelper.convertToSale(history, estimateId);
  };

  const handleSelectEstimateClick = (estimate) => {
    setEditData(estimate);
    setEstimateId(estimate.id);
    if (estimate.estimate_no) {
      const fullNo = estimate.estimate_no;
      const match = fullNo.match(/\d+$/);
      if (match) {
        const suffix = match[0];
        setEstimateNoSuffix(suffix);
        setEstimateNoPrefix(fullNo.substring(0, fullNo.length - suffix.length));
      } else {
        setEstimateNoSuffix(fullNo);
        setEstimateNoPrefix('');
      }
    } else {
      setEstimateNoSuffix('');
      setEstimateNoPrefix('');
    }
    setBillDate(new Date(estimate.time));
    setEstimateItems(
      estimate.items.map((estimateItem) => {
        let updatedItem = estimateItem?.item;
        if (updatedItem?.id) {
          const invItem = products.find((p) => p.id === updatedItem.id);
          if (invItem) {
            updatedItem = invItem;
          }
        }
        return {
          ...estimateItem,
          item: updatedItem,
          item_id: updatedItem?.id,
          name: updatedItem?.name,
          stock: updatedItem?.stock,
          waiter_id: estimateItem?.salesperson?.id || estimateItem?.waiter_id || null,
        };
      })
    );
    setSelectedCustomer(estimate?.customer);
    setSelectedWaiter(estimate?.waiter || null);
    setRemark(estimate.remark);
    setStatus(estimate.status);
    setDiscountAppliedOnItems(estimate.discount || 0);
    setHasUnsavedChanges(false);
  };

  const openCreateNewCustomerDialog = () => {
    setIsShowNewCustomerDialog(true);
  };

  const closeCreateNewCustomerDialog = () => {
    setIsShowNewCustomerDialog(false);
  };

  const selectItemHandler = (item) => {
    const selectedItem = { ...item, quantity: 1, rate: TaxManager.getItemUnitRate(item) };
    setItem(selectedItem || '');
    setItemName(item.name || '');
    setStock(item.stock || '');
    setRate(selectedItem?.rate || 0);
    setQuantity(1);
    let itemTax = TaxManager.getItemTax(selectedItem);
    let itemCess = TaxManager.getItemCess(selectedItem);
    setTax(itemTax);
    setCess(itemCess);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, CartManager.getItemAmount(selectedItem) + itemTax + itemCess));
  };

  const getItemRateValue = useCallback(
    (itemRate = rate, itemQuantity = quantity) => {
      return Number(itemRate) * itemQuantity;
    },
    [rate, quantity]
  );

  const getTaxValue = useCallback(
    (itemRate = rate, itemQuantity = quantity, itemTax = item?.tax ?? 0) => {
      return (getItemRateValue(itemRate, itemQuantity) * itemTax) / 100;
    },
    [item?.tax, getItemRateValue, rate, quantity]
  );

  const getCessValue = useCallback(
    (itemRate = rate, itemQuantity = quantity, itemCess = item?.cess ?? 0) => {
      return (getItemRateValue(Number(itemRate), itemQuantity) * itemCess) / 100;
    },
    [item?.cess, getItemRateValue, rate, quantity]
  );

  const handleChangePayable = (value) => {
    const payableVal = Number(value);
    if (rate <= 0) return;
    if (item === null) {
      setQuantity(payableVal / rate);
      setTotalAmount(payableVal);
      return;
    }

    const taxMultiplier = 1 + (item?.tax / 100 || 0);
    const cessMultiplier = 1 + (item?.cess / 100 || 0);
    const newQty = payableVal / (rate * taxMultiplier * cessMultiplier);

    setQuantity(newQty);
    setTax(getTaxValue(rate, newQty));
    setCess(getCessValue(rate, newQty));
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, payableVal));
  };

  const handleChangeRate = (value) => {
    if (quantity === 0) return;
    if (item === null) {
      const rateValue = Number(value);
      setRate(rateValue);
      setTotalAmount(rateValue * quantity);
      return;
    }
    const itemTax = getTaxValue(value);
    const itemCess = getCessValue(value);
    const itemTotal = getItemRateValue(value) + itemTax + itemCess;

    setRate(ShopsHelper.getAmountValueFormatted(shop, value));
    setTax(ShopsHelper.getAmountValueFormatted(shop, itemTax));
    setCess(ShopsHelper.getAmountValueFormatted(shop, itemCess));
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, itemTotal));
  };

  const handleChangeQty = (value) => {
    if (value === '') {
      setQuantity('');
      return;
    }
    const qty = Number(value);
    if (item === null) {
      setQuantity(qty);
      if (rate) {
        setTotalAmount(rate * qty);
      } else if (totalAmount) {
        setRate(totalAmount / qty);
      }
      return;
    }
    let itemTax = getTaxValue(rate, qty);
    let itemCess = getCessValue(rate, qty);
    setTax(itemTax);
    setCess(itemCess);
    setQuantity(qty);
    setTotalAmount(ShopsHelper.getAmountFormatted(shop, getItemRateValue(rate, qty) + itemTax + itemCess));
  };

  const handleFieldChange = (index, field, value) => {
    setEstimateItems((prevItems) =>
      prevItems.map((item, idx) => {
        if (index !== idx) return item;

        if (field === 'description') {
          return { ...item, [field]: value };
        }

        if (field === 'waiter_id') {
          return { ...item, [field]: Number(value) };
        }

        if (field === 'quantity') {
          if (value === '') {
            return { ...item, [field]: '' };
          }
          const qty = Number(value);
          if (qty <= 0) {
            return item;
          }
        }

        if (!item.item_id) {
          const updatedItem = { ...item, [field]: Number(value) };
          if (['quantity', 'rate', 'tax'].includes(field)) {
            updatedItem.payable = updatedItem.quantity * updatedItem.rate + updatedItem.tax;
          } else if (field === 'payable' && updatedItem.rate > 0) {
            updatedItem.quantity = updatedItem.payable / updatedItem.rate;
          }
          return updatedItem;
        }

        const updatedItem = { ...item, [field]: Number(value), tax: item?.item?.tax, cess: item?.item?.cess };
        if (field === 'payable' && updatedItem.rate > 0) {
          const taxMultiplier = 1 + (updatedItem.tax / 100 || 0);
          const cessMultiplier = 1 + (updatedItem.cess / 100 || 0);
          updatedItem.quantity = updatedItem.payable / (updatedItem.rate * taxMultiplier * cessMultiplier);
        }

        updatedItem.tax = TaxManager.getItemTax(updatedItem);
        updatedItem.cess = TaxManager.getItemCess(updatedItem);
        if (field !== 'payable') {
          updatedItem.payable = CartManager.getItemAmount(updatedItem) + updatedItem.tax + updatedItem.cess;
        }
        return updatedItem;
      })
    );
    setHasUnsavedChanges(true);
  };

  useEffect(() => {
    const totals = estimateItems.reduce(
      (acc, item) => {
        acc.payable += item.payable || 0;
        acc.tax += item.tax || 0;
        acc.cess += item.cess || 0;
        return acc;
      },
      { payable: 0, tax: 0, cess: 0 }
    );

    setEstimateTotals(totals);
  }, [estimateItems]);

  useEffect(() => {
    if (selectedTerminalFromContext) {
      const prefix = selectedTerminalFromContext.invoice_no_prefix || '';
      setEstimateNoPrefix(prefix);
      getNextEstimateNo(prefix);
    }
  }, [selectedTerminalFromContext, getNextEstimateNo]);

  useEffect(() => {
    if (estimateItems.length > 0) {
      const updatedItems = CartManager.spreadAndSetBillLevelDiscount(estimateItems, discountAppliedOnItems);
      setEstimateItems(updatedItems);
    }
    //eslint-disable-next-line
  }, [discountAppliedOnItems]);

  useEffect(() => {
    const loadData = async () => {
      setLoadingIndicator(true);
      try {
        const promises = [];
        if (!isMasterDataLoaded.current) {
          promises.push(getMasterData());
        }

        if (id) {
          promises.push(EstimateService.getEstimate(id));
        }

        const results = await Promise.all(promises);

        if (id) {
          const fetchedEstimateData = results[results.length - 1];
          handleSelectEstimateClick(fetchedEstimateData);
        } else {
          clearEstimateHandler();
          if (selectedTerminalFromContext) {
            getNextEstimateNo(selectedTerminalFromContext.invoice_no_prefix || '');
          }
        }

        isMasterDataLoaded.current = true;
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      } finally {
        setLoadingIndicator(false);
      }
    };

    loadData();
    //eslint-disable-next-line
  }, [id]);

  const handleChangeTerminal = (event) => {
    const selectedId = event.target.value;
    const selected = terminalsFromContext.find((t) => t.id === selectedId) || null;
    setSelectedTerminalInContext(selected);
  };

  return (
    <>
      <div className={styles.contentWrapper}>
        <div className={styles.titleSec}>
          <h2 className={styles.title}>
            {tCommon('Estimates')} <span className={styles.menuTitle}>{tCommon('Management')}</span>
          </h2>
        </div>
        <div className={styles.changeable}>
          <Grid container>
            <Grid item xs={12} sm={6}>
              <h2 className={styles.subTitle}>{editData.id ? 'Edit Estimate' : 'New Estimate'}</h2>
            </Grid>
            <Grid container item xs={12} sm={6} justifyContent="flex-end">
              <Grid item xs={5} sm={4} className={styles.printIcon}>
                {!editData?.id && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#ff851b' }}
                    onClick={() => history.push(ROUTES.ESTIMATE_REPORT)}
                  >
                    <NoteAddIcon className={styles.actionBtnIcon} />
                    {tCommon('Estimate Report')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid container justify="flex-end" alignItems="flex-start" pt={2} rowSpacing={2} columnSpacing={6}>
            <Grid item container xs={12} sm={6} justify="flex-end" alignItems="flex-start" pt={2} rowSpacing={2}>
              {!editData.id && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="terminal-select-label">{tCommon('Terminal')}</InputLabel>
                    <Select
                      size="small"
                      labelId="terminal-select-label"
                      value={selectedTerminalFromContext?.id ?? ''}
                      onChange={(e) => {
                        handleChangeTerminal(e);
                        setHasUnsavedChanges(true);
                      }}
                      label={tOperations('Terminal')}
                      style={{ height: 42 }}
                    >
                      <MenuItem value={null}>
                        <Typography>{tCommon('None')}</Typography>
                      </MenuItem>
                      {terminalsFromContext.map((terminal) => (
                        <MenuItem key={terminal.id} value={terminal.id}>
                          {terminal.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {((!editData.id && selectedTerminalFromContext) || editData.id) && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    value={estimateNoSuffix}
                    label={tCommon('Estimate Number')}
                    variant="outlined"
                    size="small"
                    onChange={(event) => {
                      setEstimateNoSuffix(event.target.value);
                      setHasUnsavedChanges(true);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {estimateNoPrefix}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <DatePicker
                        label={tCommon('Date')}
                        value={billDate}
                        onChange={(date) => setBillDate(date)}
                        disableFuture
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined",
                            className: styles.dateBox,
                            size: "small",
                            InputProps:{
                              style: { paddingRight: 4 }, 
                            },
                          },
                          openPickerButton: {
                            'aria-label': 'change date',
                            sx: { pr: 2},
                          },
                        }} 
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TimePicker
                        label={tCommon('Time')}
                        value={billDate}
                        onChange={(date) => setBillDate(date)}
                        disableFuture
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant:"outlined",
                            size: "small",
                            className: styles.dateBox,
                            InputProps: {
                              style: { paddingRight: 4 },
                            }, 
                          },
                          openPickerButton: {
                            'aria-label': 'change time',
                            sx: { p: 2 },
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </Grid>
            </Grid>
            <Grid item container xs={12} sm={6} justify="flex-end" alignItems="flex-start" pt={2} rowSpacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  value={salespersons.find((s) => s.id === selectedWaiter?.id) || null}
                  onChange={(event, newValue) => {
                    setSelectedWaiter(newValue || null);
                    setHasUnsavedChanges(true);
                  }}
                  options={salespersons}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField {...params} label={tCommon('Salesperson')} variant="outlined" size="small" />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>{tCommon('Status')}</InputLabel>
                  <Select
                    size="small"
                    value={status}
                    onChange={(event) => setStatus(event.target.value)}
                    label={tCommon('Status')}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="converted">Converted to Sale</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  value={customers.find((c) => c.id === selectedCustomer?.id) || null}
                  onChange={(event, newValue) => {
                    if (newValue?.id === 'create-new') {
                      openCreateNewCustomerDialog();
                    } else {
                      setSelectedCustomer(newValue || null);
                      setHasUnsavedChanges(true);
                    }
                  }}
                  options={[{ id: 'create-new', name: tCommon('Create New Customer') }, ...customers]}
                  getOptionLabel={(option) => option.name || ''}
                  filterOptions={(options, { inputValue }) => {
                    const cleanInput = inputValue.trim().toLowerCase();
                    return options.filter((option) => {
                      if (option.id === 'create-new') return true;
                      return (
                        (option.name && option.name.toLowerCase().includes(cleanInput)) ||
                        (option.mobile && option.mobile.toLowerCase().includes(cleanInput)) ||
                        (option.location && option.location.toLowerCase().includes(cleanInput)) ||
                        (option.code && option.code.toLowerCase().includes(cleanInput))
                      );
                    });
                  }}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    if (option.id === 'create-new') {
                      return (
                        <MenuItem
                          key={key}
                          {...optionProps}
                          className={styles.createNewOption}
                        >
                          <AddCircleOutlineIcon className={styles.createNewOptionIcon} style={{ marginRight: 8 }} />
                          {option.name}
                        </MenuItem>
                      );
                    }
                    return (
                      <MenuItem
                        key={key}
                        {...optionProps}
                        className={styles.dropdownOption}
                      >
                        {option.name}
                      </MenuItem>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tCommon('Customer')}
                      variant="outlined"
                      size="small"
                      helperText={
                        selectedCustomer && (
                          <>
                            <span>
                              {tCommon('Location')}: {selectedCustomer.location}, {tOperations('Phone')}:{' '}
                              {selectedCustomer.mobile}
                            </span>
                            <br />
                            <span>
                              {tCommon('Balance')}: {formatBalanceWithCr(selectedCustomer.balance)}, {tCommon('points')}
                              : {selectedCustomer.points || 0}
                            </span>
                          </>
                        )
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </div>
        <div className={styles.changeable} style={{ boxShadow: 'none', padding: '10px 0', overflowX: 'auto' }}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ minWidth: '250px' }}>{tCommon('Product')}</TableCell>
                  <TableCell style={{ minWidth: '120px' }}>{tCommon('Description')}</TableCell>
                  <TableCell style={{ minWidth: '80px' }}>Rate</TableCell>
                  <TableCell style={{ minWidth: '80px' }}>Qty</TableCell>
                  <TableCell style={{ minWidth: '80px' }}>Tax</TableCell>
                  <TableCell style={{ minWidth: '120px' }}>Payable</TableCell>
                  <TableCell style={{ minWidth: '50px' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Autocomplete
                      value={item}
                      onChange={(event, newValue) => (newValue ? selectItemHandler(newValue) : resetItemFields())}
                      inputValue={itemName}
                      onInputChange={(e, value) => setItemName(value)}
                      getOptionLabel={(option) => `${option?.name}`}
                      options={products}
                      renderInput={(params) => (
                        <TextField {...params} label={tCommon('Product')} variant="outlined" size="small" />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <NumberField value={rate} onChange={(e) => handleChangeRate(e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <NumberField
                      value={quantity}
                      onChange={(e) => handleChangeQty(e.target.value)}
                      helperText={
                        item && (
                          <span style={{ fontSize: '10px', color: '#718096', fontWeight: 500 }}>Stock: {stock}</span>
                        )
                      }
                      FormHelperTextProps={{
                        style: { position: 'absolute', top: '100%', margin: 0, marginTop: '2px', whiteSpace: 'nowrap' },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {ShopsHelper.getAmountFormatted(shop, Number(tax) + Number(cess) || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <NumberField value={totalAmount} onChange={(event) => handleChangePayable(event.target.value)} />
                  </TableCell>
                  <TableCell>
                    <IconButton disabled={quantity <= 0} onClick={addItem} color="primary">
                      <LibraryAddIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
                {estimateItems.map((estimateItem, index) => (
                  <TableRow key={index}>
                    <TableCell>{estimateItem.name}</TableCell>
                    <TableCell>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={estimateItem.description}
                        onChange={(event) => handleFieldChange(index, 'description', event.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <NumberField
                        value={estimateItem.rate}
                        onChange={(event) => handleFieldChange(index, 'rate', event.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <NumberField
                        value={estimateItem.quantity}
                        onChange={(event) => handleFieldChange(index, 'quantity', event.target.value)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">
                        {ShopsHelper.getAmountFormatted(
                          shop,
                          Number(estimateItem.tax) + Number(estimateItem.cess) || 0
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <NumberField
                        value={estimateItem.payable}
                        onChange={(event) => handleFieldChange(index, 'payable', event.target.value)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => deleteItem(index)} color="secondary">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        <div className={styles.changeable}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                id="outlined-multiline-static"
                label={tOperations('Remark')}
                multiline
                rows={6}
                variant="outlined"
                fullWidth
                value={remark}
                onChange={(event) => {
                  setRemark(event.target.value);
                  setHasUnsavedChanges(true);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TableContainer component={Paper} elevation={0}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>{tCommon('Total Amount')}</TableCell>
                      <TableCell align="right" style={{ fontWeight: 600 }}>
                        {ShopsHelper.getAmountFormatted(shop, estimateTotals.payable + discountAppliedOnItems)}
                      </TableCell>
                    </TableRow>
                    {shop?.tax_region === COUNTRY_CONFIGRATION.IN.name ? (
                      <>
                        <TableRow>
                          <TableCell>CGST</TableCell>
                          <TableCell align="right" style={{ fontWeight: 600 }}>
                            {ShopsHelper.getAmountFormatted(shop, (estimateTotals.tax + estimateTotals.cess) / 2)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>SGST</TableCell>
                          <TableCell align="right" style={{ fontWeight: 600 }}>
                            {ShopsHelper.getAmountFormatted(shop, (estimateTotals.tax + estimateTotals.cess) / 2)}
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell>{tCommon('Total Tax')}</TableCell>
                        <TableCell align="right" style={{ fontWeight: 600 }}>
                          {ShopsHelper.getAmountFormatted(shop, estimateTotals.tax + estimateTotals.cess)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>{tCommon('Discount')}</TableCell>
                      <TableCell align="right">
                        <NumberField
                          value={discountAppliedOnItems}
                          onChange={(e) => {
                            setDiscountAppliedOnItems(Number(e.target.value));
                            setHasUnsavedChanges(true);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Typography variant="h6" color="primary">
                          {tCommon('Payable')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" color="primary" style={{ fontWeight: 600 }}>
                          {ShopsHelper.getAmountFormatted(shop, estimateTotals.payable)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          <Grid
            container
            className={styles.submitSection}
            justify="flex-end"
            spacing={2}
            style={{ marginTop: '20px', paddingBottom: '20px' }}
          >
            <Button variant="contained" color="secondary" onClick={clearEstimateHandler} style={{ marginRight: '8px' }}>
              {tCommon('Cancel')}
            </Button>
            {editData.id && status !== 'converted' && (
              <Button
                variant="contained"
                color="secondary"
                style={{ marginRight: '8px', backgroundColor: '#00a65a', color: '#fff' }}
                onClick={handleConvertToSale}
                disabled={loadingIndicator || hasUnsavedChanges}
              >
                Convert to Sale
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              onClick={handleSaveEstimate}
              disabled={loadingIndicator}
            >
              {editData.id ? 'Update Estimate' : 'Save Estimate'}
            </Button>
          </Grid>
        </div>
      </div>
      {updateSuccessData && (
        <EstimateUpdateSuccessDialog
          handleClose={() => {
            if (updateSuccessData.action === 'Create') {
              clearEstimateHandler();
            }
            setUpdateSuccessData(null);
          }}
          data={updateSuccessData}
          printFn={(estNo) => handlePrint(updateSuccessData.estimate_id || estNo)}
        />
      )}
      {isShowNewCustomerDialog && (
        <NewCustomerDialog
          closeCreateNewCustomerDialog={closeCreateNewCustomerDialog}
          reloadCustomers={getCustomers}
          customerGroups={customerGroups}
        />
      )}
      {estimateToPrint &&
        createPortal(
          <PrintDocument
            type={PRINT_TYPES.ESTIMATE}
            data={estimateToPrint}
            shop={shop}
            billFormat={billFormatToPrint}
            totals={totalsToPrint}
            paperSize={paperSize}
          />,
          document.body
        )}
    </>
  );
}

export default withConsoleBase(AddEstimate);
